document.addEventListener('DOMContentLoaded', () => {
  // 1. SELECT DOM ELEMENTS
  const button = document.querySelector('.ja-product-atf-product-actions__fullscreen');
  const mainImage = document.getElementById('main-product-image');
  const overlay = document.querySelector('.yip-product-lightbox-overlay');
  const img = document.querySelector('.yip-product-lightbox-img');
  const closeBtn = document.querySelector('.yip-product-lightbox-close');
  const container = img.parentElement;

  // 2. SAFETY CHECK
  if (!button || !mainImage || !overlay || !img || !closeBtn || !container) {
    console.warn('Lightbox Error: One or more required elements were not found.');
    return;
  }

  // 3. STATE AND CONFIGURATION
  const MIN_SCALE = 1;
  const MAX_SCALE = 5;
  const ZOOM_FACTOR_WHEEL = 1.1;
  const DOUBLE_TAP_ZOOM_SCALE = 3;
  const DOUBLE_TAP_DELAY = 300;
  const DRAG_THRESHOLD = 5;
  const ANIMATION_DURATION = 300;

  let isModalOpen = false;
  let isPointerDown = false;
  let isDragging = false;
  let isPinching = false;
  let lastTapTime = 0;
  let triggerElement = null;
  let activePointerId = null;

  // State for positioning. These are now set ONLY when needed.
  let currentScale = 1;
  let translateX = 0, translateY = 0;
  let initialX, initialY, startX, startY;
  let pinchStartDistance = 0;

  // Preload cache for performance
  const preloadCache = new Map();

  // 4. CORE FUNCTIONS

  /**
   * This function is the single source of truth for positioning.
   * It is called ONLY from img.onload or on window resize when the modal is open.
   * It reads dimensions at the last possible moment to ensure they are correct.
   */
  function positionImage() {
    // Read dimensions at the last possible moment.
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    if (!containerWidth || !containerHeight || !img.naturalWidth) {
      // Failsafe if dimensions are still not ready
      console.warn("Could not get container dimensions to position image.");
      return;
    }

    const imgRatio = img.naturalWidth / img.naturalHeight;
    const containerRatio = containerWidth / containerHeight;
    
    let renderedWidth, renderedHeight;
    if (imgRatio > containerRatio) {
      renderedWidth = containerWidth;
      renderedHeight = containerWidth / imgRatio;
    } else {
      renderedHeight = containerHeight;
      renderedWidth = containerHeight * imgRatio;
    }
    
    // Reset scale and translation based on fresh calculations
    currentScale = 1;
    translateX = (containerWidth - renderedWidth) / 2;
    translateY = (containerHeight - renderedHeight) / 2;
    
    // Apply the transform directly
    img.style.transition = 'none'; // Ensure no stray transitions interfere
    img.style.transform = `translate(${translateX}px, ${translateY}px) scale(${currentScale})`;
  }

  /**
   * Applies the current transform (translation and scale) to the image.
   * It constrains panning to keep the image within the viewport when zoomed.
   */
  function applyTransform() {
    // Read fresh dimensions every time a transform is applied
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    
    if (!img.naturalWidth) return; // Failsafe

    const imgRatio = img.naturalWidth / img.naturalHeight;
    const containerRatio = containerWidth / containerHeight;
    
    let renderedWidth;
    if (imgRatio > containerRatio) {
      renderedWidth = containerWidth;
    } else {
      renderedWidth = containerHeight * imgRatio;
    }
    
    const renderedHeight = renderedWidth / imgRatio;

    if (currentScale <= MIN_SCALE) {
      // Recenter if zoomed out fully
      translateX = (containerWidth - renderedWidth) / 2;
      translateY = (containerHeight - renderedHeight) / 2;
    } else {
      // Constrain panning to keep the image within the viewport
      const scaledImageWidth = renderedWidth * currentScale;
      const scaledImageHeight = renderedHeight * currentScale;
      
      const minX = containerWidth - scaledImageWidth;
      const minY = containerHeight - scaledImageHeight;
      
      // Allow panning only up to the edges of the image
      translateX = Math.max(minX, Math.min(0, translateX));
      translateY = Math.max(minY, Math.min(0, translateY));
    }
    
    img.style.transform = `translate(${translateX}px, ${translateY}px) scale(${currentScale})`;
  }

  /**
   * Resets the image to its initial 1x scale and centered position.
   * @param {boolean} useTransition - Whether to animate the reset.
   */
  function resetZoomAndPan(useTransition = false) {
    if (useTransition) {
      img.style.transition = `transform ${ANIMATION_DURATION}ms cubic-bezier(0.25, 0.8, 0.25, 1)`;
      positionImage(); // Reposition using the master function
      setTimeout(() => {
        img.style.transition = 'none';
      }, ANIMATION_DURATION);
    } else {
      img.style.transition = 'none';
      positionImage();
    }
  }
  
  /**
   * Loads the high-resolution image into the lightbox.
   * The critical positioning logic is in the `onload` handler.
   * @param {string} highResSrc - The URL for the full-size image.
   * @param {string} fallbackSrc - The URL for the smaller, visible image to use if the high-res fails.
   */
  function updateLightboxImage(highResSrc, fallbackSrc) {
    // Clear previous state and set loading visuals if desired
    img.src = ''; // Prevents showing a stale image
    img.alt = `${mainImage.alt || 'Product'} - zoomed view`;

    img.onload = () => {
      // By now, the modal is visible and the image is loaded.
      positionImage();
      img.onload = img.onerror = null; // Clean up listeners
    };

    img.onerror = () => {
      console.warn(`Lightbox: High-res image failed to load: ${highResSrc}. Falling back.`);
      img.src = fallbackSrc; // Try the smaller image
      img.onload = () => {
        positionImage(); // Position the fallback
        img.onload = img.onerror = null;
      };
      img.onerror = () => {
        console.error('Lightbox: All image sources failed.');
        img.onload = img.onerror = null;
      }
    };

    // Start the load
    img.src = highResSrc;
  }

  /**
   * Utility to preload an image and cache the promise.
   * @param {string} src - The image URL to load.
   * @returns {Promise<HTMLImageElement>}
   */
  function loadImage(src) {
    if (!src) return Promise.reject("No src provided to loadImage");
    if (preloadCache.has(src)) return preloadCache.get(src);
    const promise = new Promise((resolve, reject) => {
      const loader = new Image();
      loader.onload = () => resolve(loader);
      loader.onerror = reject;
      loader.src = src;
    });
    preloadCache.set(src, promise);
    return promise;
  }

  /**
   * Preloads the high-resolution version of the currently visible main product image.
   */
  function preloadCurrentImage() {
    const highResSrc = mainImage.dataset.fullsize || (mainImage.src ? mainImage.src.replace(/_(\d+)x(\d+)\./, '_1024x1024.') : '');
    if (highResSrc) {
        loadImage(highResSrc).catch(err => console.warn("Preload failed", err));
    }
  }
  
  // Initial preload and observer setup to watch for slider changes
  preloadCurrentImage();
  const imageObserver = new MutationObserver(() => setTimeout(preloadCurrentImage, 50));
  if (mainImage) {
    imageObserver.observe(mainImage, { attributes: true, attributeFilter: ['src'] });
  }

  /**
   * Opens the lightbox modal. This function is now simplified to only handle visibility.
   * The actual image loading and positioning is delegated to `updateLightboxImage`.
   */
  function openModal() {
    if (isModalOpen) return;

    triggerElement = document.activeElement;
    const fallbackSrc = mainImage.src;
    const highResSrc = mainImage.dataset.fullsize || (fallbackSrc ? fallbackSrc.replace(/_(\d+)x(\d+)\./, '_1024x1024.') : '');

    if (!highResSrc) {
        console.error("Lightbox: No image source found to open.");
        return;
    }

    document.documentElement.style.touchAction = 'none';
    document.body.style.overflow = 'hidden';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    
    overlay.classList.add('is-visible');
    isModalOpen = true;

    // ✅ Re-introduce the wait for the browser to render the modal.
    // This gives the CSS transition time to start and for the container to get its dimensions.
    requestAnimationFrame(() => {
        // A second frame is often needed for reliability across browsers
        requestAnimationFrame(() => {
            if (!isModalOpen) return; // Check if modal was closed during the wait
            // Now that the modal is visible and has dimensions, start loading the image.
            // The onload handler will then handle the final positioning.
            updateLightboxImage(highResSrc, fallbackSrc);
        });
    });
  }

  /**
   * Closes the lightbox modal and resets its state.
   */
  function closeModal() {
    if (!isModalOpen) return;

    if (activePointerId && img.hasPointerCapture(activePointerId)) {
      try { img.releasePointerCapture(activePointerId); } catch (_) {}
    }
    
    overlay.classList.remove('is-visible');
    isModalOpen = false;
    document.body.style.overflow = '';
    document.documentElement.style.touchAction = '';
    isPointerDown = isDragging = isPinching = false;
    triggerElement?.focus();
    
    // Reset image src to prevent flash of old content on next open
    setTimeout(() => {
        if (!isModalOpen) img.src = '';
    }, 300); // 300ms matches CSS transition
  }

  // 5. EVENT LISTENERS

  function getDistance(t1, t2) { return Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY); }

  function handleDoubleTap(e) {
    if (currentScale > MIN_SCALE) {
      resetZoomAndPan(true);
    } else {
      currentScale = DOUBLE_TAP_ZOOM_SCALE;
      const rect = container.getBoundingClientRect();
      const pointX = e.clientX - rect.left;
      const pointY = e.clientY - rect.top;
      
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      translateX = containerWidth / 2 - pointX * currentScale;
      translateY = containerHeight / 2 - pointY * currentScale;

      img.style.transition = `transform ${ANIMATION_DURATION}ms cubic-bezier(0.25, 0.8, 0.25, 1)`;
      applyTransform();
      setTimeout(() => { img.style.transition = 'none'; }, ANIMATION_DURATION);
    }
  }

  // --- Pointer, Wheel, and Touch Handlers ---

  img.addEventListener('click', (e) => {
    if (isDragging) return;
    const now = Date.now();
    if (now - lastTapTime < DOUBLE_TAP_DELAY) {
      e.preventDefault();
      handleDoubleTap(e);
    }
    lastTapTime = now;
  });

  img.addEventListener('pointerdown', (e) => {
    if (currentScale <= MIN_SCALE || !e.isPrimary) return;
    e.preventDefault();
    img.style.transition = 'none';
    isPointerDown = true;
    initialX = e.clientX;
    initialY = e.clientY;
    startX = e.clientX;
    startY = e.clientY;
    img.setPointerCapture(e.pointerId);
    activePointerId = e.pointerId;
  });

  img.addEventListener('pointermove', (e) => {
    if (!isPointerDown || !e.isPrimary) return;
    if (!isDragging) {
      const dxFromStart = Math.abs(e.clientX - initialX);
      const dyFromStart = Math.abs(e.clientY - initialY);
      if (dxFromStart > DRAG_THRESHOLD || dyFromStart > DRAG_THRESHOLD) {
        isDragging = true;
        img.style.cursor = 'grabbing';
      }
    }
    if (isDragging) {
      translateX += e.clientX - startX;
      translateY += e.clientY - startY;
      startX = e.clientX;
      startY = e.clientY;
      applyTransform();
    }
  });

  const stopPointerInteraction = (e) => {
    if (!e.isPrimary) return;
    if (isPointerDown) {
      img.releasePointerCapture(e.pointerId);
      img.style.cursor = currentScale > 1 ? 'grab' : 'zoom-in';
    }
    isPointerDown = false;
    activePointerId = null;
    setTimeout(() => { isDragging = false; }, 0);
  };

  img.addEventListener('pointerup', stopPointerInteraction);
  img.addEventListener('pointercancel', stopPointerInteraction);
  img.addEventListener('pointerleave', stopPointerInteraction);

  container.addEventListener('wheel', (e) => {
    e.preventDefault();
    img.style.transition = 'none';
    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const pointX = (mouseX - translateX) / currentScale;
    const pointY = (mouseY - translateY) / currentScale;

    const delta = e.deltaY < 0 ? ZOOM_FACTOR_WHEEL : 1 / ZOOM_FACTOR_WHEEL;
    const newScale = Math.min(Math.max(currentScale * delta, MIN_SCALE), MAX_SCALE);
    
    translateX -= pointX * (newScale - currentScale);
    translateY -= pointY * (newScale - currentScale);

    currentScale = newScale;
    applyTransform();
  }, { passive: false });

  img.addEventListener('touchstart', (e) => {
      if (e.touches.length === 2) {
          isPinching = true;
          pinchStartDistance = getDistance(e.touches[0], e.touches[1]);
      }
  }, { passive: true });

  img.addEventListener('touchmove', (e) => {
      if (isPinching && e.touches.length === 2) {
          e.preventDefault();
          const currentDistance = getDistance(e.touches[0], e.touches[1]);
          const scaleDelta = currentDistance / pinchStartDistance;
          
          const rect = container.getBoundingClientRect();
          const pinchCenterX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
          const pinchCenterY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
          
          const mouseX = pinchCenterX - rect.left;
          const mouseY = pinchCenterY - rect.top;
          
          const pointX = (mouseX - translateX) / currentScale;
          const pointY = (mouseY - translateY) / currentScale;

          const newScale = Math.min(Math.max(currentScale * scaleDelta, MIN_SCALE), MAX_SCALE);
          
          translateX -= pointX * (newScale - currentScale);
          translateY -= pointY * (newScale - currentScale);
          
          currentScale = newScale;
          pinchStartDistance = currentDistance;
          applyTransform();
      }
  }, { passive: false });

  img.addEventListener('touchend', (e) => {
      if (e.touches.length < 2) { isPinching = false; }
  });

  // Final setup of triggers
  if (button) {
    button.addEventListener('click', openModal);
  }
  if (mainImage) {
    mainImage.addEventListener('click', (e) => {
        if (window.getSelection().toString() === '') {
            openModal();
        }
    });
  }
  if(closeBtn) {
    closeBtn.addEventListener('click', closeModal);
  }
  if(overlay) {
    overlay.addEventListener('click', (e) => { if (e.target === overlay) closeModal(); });
  }
  document.addEventListener('keydown', (e) => { if (isModalOpen && e.key === 'Escape') closeModal(); });
  window.addEventListener('resize', () => { if (isModalOpen) { positionImage(); } });
});
